import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTE_CONSTANTS } from "../constants/routeConstants";
import { DataContext } from "../dataContext";

const {
  HISTORY_PAGE,
  SETTINGS,
  PEOPLE,
  MAIN_PAGE,
  EVENT_DEMO,
  SCHEDULE_PAYMENT,
  SCHEDULED_PAYMENTS,
  SIGN_OUT,
  PAYMENT_REQUESTS,
} = ROUTE_CONSTANTS;

// Lucide-style line glyphs, 17px at stroke 1.8 — drawn inline so they pick up
// `currentColor` from the icon button.
const glyph = {
  list: (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="8" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="20" y2="12" />
      <line x1="8" y1="18" x2="20" y2="18" />
      <line x1="4" y1="6" x2="4.01" y2="6" />
      <line x1="4" y1="12" x2="4.01" y2="12" />
      <line x1="4" y1="18" x2="4.01" y2="18" />
    </svg>
  ),
  requests: (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="12" y1="4" x2="12" y2="16" />
      <polyline points="6 11 12 17 18 11" />
      <line x1="5" y1="20" x2="19" y2="20" />
    </svg>
  ),
  settings: (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="4" y1="8" x2="20" y2="8" />
      <line x1="4" y1="16" x2="20" y2="16" />
      <circle cx="10" cy="8" r="2.6" fill="var(--fill)" />
      <circle cx="15" cy="16" r="2.6" fill="var(--fill)" />
    </svg>
  ),
  calendar: (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="8" y1="3" x2="8" y2="6" />
      <line x1="16" y1="3" x2="16" y2="6" />
    </svg>
  ),
  handshake: (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m11 17 2 2a1 1 0 1 0 3-3" />
      <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
      <path d="m21 3 1 11h-2" />
      <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
      <path d="M3 4h8" />
    </svg>
  ),
  house: (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 11 12 3l9 8" />
      <path d="M5 10v10h14V10" />
      <path d="M10 20v-6h4v6" />
    </svg>
  ),
  chevronLeft: (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 6 9 12 15 18" />
    </svg>
  ),
};

const NavBar = ({ branding, homePath }) => {
  const navigate = useNavigate();
  const {
    scheduledAlert,
    p2pConfig,
    paymentRequests,
    locallyHandledRequestIds,
  } = useContext(DataContext);

  const [loginType] = useState(window.localStorage.getItem("loginType"));

  const path = window.location.pathname;

  // Count of incoming RfPs awaiting the user's action. Drives the badge on
  // the payment-requests button. paymentRequests comes from
  // GET /v1/embedded/request/pending — the backend already filters to
  // incoming RfP rows in WaitForRequesteeApproval / WaitForRequesteeCardInput
  // states. Exclude anything the user optimistically declined locally so the
  // badge drops instantly, not after getHistory syncs.
  const pendingRequestCount = (paymentRequests || []).filter(
    (t) => !(locallyHandledRequestIds || []).includes(t.id)
  ).length;

  const notificationIcon = () =>
    p2pConfig?.request_for_pay ? (
      <button
        type="button"
        aria-label="Payment requests"
        className="np-icon-btn nav_icon"
        onClick={() => navigate(PAYMENT_REQUESTS)}
        data-cy="payment_requests_icon"
      >
        {glyph.requests}
        {pendingRequestCount > 0 && (
          <span className="np-badge">
            {pendingRequestCount > 9 ? "9+" : pendingRequestCount}
          </span>
        )}
      </button>
    ) : null;

  // FI logo slot. The sending branding payload has no logo today, so the pill
  // only renders when one is present — an empty pill would read as a bug.
  const brandSlot = () => {
    const logoUrl = branding?.sending_logo_url;
    if (typeof logoUrl === "string" && logoUrl.length > 0) {
      return (
        <div className="np-brand">
          <img src={logoUrl} alt="" />
        </div>
      );
    }
    return null;
  };

  const sendNav = (switchIcon, isDemoApp) => {
    return (
      <>
        <nav>
          {brandSlot()}
          <span className="np-nav-spacer" />
          {switchIcon}
          <button
            type="button"
            aria-label="History"
            className="np-icon-btn nav_icon"
            onClick={() => navigate(HISTORY_PAGE)}
            data-cy="transfer_history_icon"
          >
            {glyph.list}
          </button>
          {notificationIcon()}
          {!isDemoApp && (
            <>
              {p2pConfig.scheduled_recurring_enabled && (
                <button
                  type="button"
                  aria-label="Calendar"
                  className="np-icon-btn nav_icon"
                  onClick={() => navigate(SCHEDULED_PAYMENTS)}
                  data-cy="scheduled_transfers_icon"
                >
                  {glyph.calendar}
                  {scheduledAlert && <span className="np-dot" />}
                </button>
              )}
              <button
                type="button"
                aria-label="People"
                className="np-icon-btn nav_icon"
                onClick={() => navigate(PEOPLE)}
                data-cy="people_icon"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="9" cy="8" r="3.5" />
                  <path d="M2.5 20c.8-3.6 3.4-5.5 6.5-5.5s5.7 1.9 6.5 5.5" />
                  <path d="M15.5 4.8a3.5 3.5 0 0 1 0 6.4M17.5 14.8c2 .7 3.4 2.4 4 5.2" />
                </svg>
              </button>
              <button
                type="button"
                aria-label="Settings"
                className="np-icon-btn nav_icon"
                onClick={() => navigate(SETTINGS)}
                data-cy="settings_icon"
              >
                {glyph.settings}
              </button>
            </>
          )}

          {loginType !== "sso" && (
            <button
              type="button"
              aria-label="Log out"
              className="np-pill nav_icon"
              onClick={() => navigate(SIGN_OUT)}
            >
              Log Out
            </button>
          )}
        </nav>
        <div className="np-title-block">
          <h1 className="np-title">Send Money</h1>
        </div>
      </>
    );
  };

  const genericNav = (title, navigateTo) => {
    return (
      <>
        <nav>
          <button
            type="button"
            aria-label="Go back"
            className="np-pill nav_icon"
            onClick={() => navigate(navigateTo)}
            data-cy="go_back_icon"
          >
            {glyph.chevronLeft}
            Back
          </button>
        </nav>
        <div className="np-title-block">
          <h2 className="np-title">{title}</h2>
        </div>
      </>
    );
  };

  const toDemoIcon = () => {
    return (
      <>
        {branding.event_demo && (
          <button
            type="button"
            aria-label="Demo"
            className="np-icon-btn nav_icon"
            onClick={() => navigate(EVENT_DEMO)}
          >
            {glyph.handshake}
          </button>
        )}
      </>
    );
  };

  const toHomeIcon = () => {
    return (
      <button
        type="button"
        aria-label="Home"
        className="np-icon-btn nav_icon"
        onClick={() => navigate(MAIN_PAGE)}
      >
        {glyph.house}
      </button>
    );
  };

  const pathOrTrailingSlash = (route) => {
    return path === route || path === route.concat("/");
  };

  return (
    <>
      {(() => {
        switch (true) {
          case pathOrTrailingSlash(MAIN_PAGE):
            return <>{sendNav(toDemoIcon(), false)}</>;
          case pathOrTrailingSlash(EVENT_DEMO) && branding.event_demo:
            return <>{sendNav(toHomeIcon(), true)}</>;
          case pathOrTrailingSlash(HISTORY_PAGE):
            return <>{genericNav("Transfers", homePath)}</>;
          case pathOrTrailingSlash(PAYMENT_REQUESTS):
            return <>{genericNav("Payment requests", homePath)}</>;
          case path.includes("/payment-request/"):
            return <>{genericNav("Payment Request", PAYMENT_REQUESTS)}</>;
          case pathOrTrailingSlash(SETTINGS):
            return <>{genericNav("Settings", MAIN_PAGE)}</>;
          case pathOrTrailingSlash(PEOPLE):
            return <>{genericNav("People", MAIN_PAGE)}</>;
          case pathOrTrailingSlash(SCHEDULE_PAYMENT) &&
            p2pConfig.scheduled_recurring_enabled:
            return <>{genericNav("Schedule payment", homePath)}</>;
          case pathOrTrailingSlash(SCHEDULED_PAYMENTS) &&
            p2pConfig.scheduled_recurring_enabled:
            return <>{genericNav("Scheduled payments", homePath)}</>;
          case path.includes("default-account"):
            return <>{genericNav("Set default card", SETTINGS)}</>;
          case path.includes("ing-details/") && !path.includes("cancel"):
            return <>{genericNav("Transfer Detail", HISTORY_PAGE)}</>;
          default:
            return null;
        }
      })()}
    </>
  );
};

export default NavBar;
